import { useErrorContext } from "@context-providers/error-boundary/error-handling-context";
import {
  IntegrationDialogCallbacks,
  IntegrationUserConnectionOrigin,
  UserLevelIntegrationDialog,
} from "@custom-types/integrations-types";
import { ReactSetStateFunction } from "@custom-types/types";
import { useToast } from "@flat-ui";
import { TokensService } from "@services/integrations-service/tokens-service";
import { FaroCoreApiConst, FaroCoreApiTypes } from "@stellar/core-api-client";
import { UserProfile } from "@utils/track-event/track-event-list";
import { useTrackEvent } from "@utils/track-event/use-track-event";
import { useCallback, useEffect, useMemo, useState } from "react";

interface Props {
  /** ID of the selected integration */
  integrationId: FaroCoreApiTypes.IntegrationId;

  /** Instance of the tokens service */
  tokensService: TokensService;
}

export interface ReturnProps extends IntegrationDialogCallbacks {
  /** Dialog state */
  dialogState: UserLevelIntegrationDialog;

  /** Callback to update the dialog state */
  setDialogState: ReactSetStateFunction<UserLevelIntegrationDialog>;

  /** All the integrations */
  allIntegrations: {
    /** All the enabled integrations */
    enabledIntegrationIds: Set<FaroCoreApiTypes.IntegrationId>;

    /** All the available integrations */
    availableIntegrationIds: FaroCoreApiTypes.IntegrationId[];
  };

  /** Enable an integration */
  onEnableIntegration: (
    integrationId: FaroCoreApiTypes.IntegrationId
  ) => Promise<void>;

  /** Flag to determine whether the integration still loading */
  isLoadingIntegrations: boolean;
}

/** @returns properties and methods commonly used for integration workflows at user level */
export function useUserIntegrations({
  integrationId,
  tokensService,
}: Props): ReturnProps {
  const [dialogState, setDialogState] =
    useState<UserLevelIntegrationDialog>("none");

  const [tokens, setTokens] = useState<FaroCoreApiTypes.IntegrationToken[]>([]);
  const [isLoadingIntegrations, setIsLoadingIntegrations] =
    useState<boolean>(true);

  const { handleErrorWithToast } = useErrorContext();
  const { openToast } = useToast();
  const { trackEvent } = useTrackEvent();

  /** Filtering the available integrations and enabled integrations */
  const allIntegrations = useMemo(() => {
    const integrationIds = FaroCoreApiConst.allIntegrationIds;

    const enabledIntegrationSet = new Set(
      integrationIds.filter((id) =>
        tokens.some((token) => token.provider === id)
      )
    );

    return {
      enabledIntegrationIds: enabledIntegrationSet,
      availableIntegrationIds: integrationIds,
    };
  }, [tokens]);

  /** Fetches all integration tokens from backend through the integration service */
  useEffect(() => {
    async function getIntegrationTokens(): Promise<void> {
      try {
        const integrationTokens = await tokensService.getIntegrationTokens();
        setTokens(integrationTokens);
      } catch (error) {
        handleErrorWithToast({
          id: `getIntegrationTokens-${Date.now().toString()}`,
          title: "Failed to get the integration tokens.",
          error,
        });
      } finally {
        setIsLoadingIntegrations(false);
      }
    }
    void getIntegrationTokens();
  }, [handleErrorWithToast, tokensService]);

  /** Enable an integration */
  const onEnableIntegration = useCallback(
    async (integrationId: FaroCoreApiTypes.IntegrationId): Promise<void> => {
      trackEvent({
        name: UserProfile.connectIntegration,
        props: {
          integrationId,
          from: IntegrationUserConnectionOrigin.integrationAccounts,
        },
      });

      setDialogState("inProgress");

      try {
        const integrationTokens = await tokensService.authorizeIntegration(
          integrationId
        );
        setTokens(integrationTokens);
        setDialogState("success");
      } catch (error) {
        handleErrorWithToast({
          id: `authorizeIntegration-${Date.now().toString()}`,
          title: "Failed to authorize the integration.",
          error,
        });
        setDialogState("none");
      }
    },
    [handleErrorWithToast, tokensService, trackEvent]
  );

  /** Disable an integration */
  const onDisableIntegration = useCallback(
    async (integrationId: FaroCoreApiTypes.IntegrationId): Promise<void> => {
      trackEvent({
        name: UserProfile.disconnectIntegration,
        props: {
          integrationId,
          from: IntegrationUserConnectionOrigin.integrationAccounts,
        },
      });

      setDialogState("disconnect");

      try {
        const integrationTokens = await tokensService.revokeAuthorization(
          integrationId
        );
        setTokens(integrationTokens);
        openToast({
          title: "Integration disconnected",
        });
      } catch (error) {
        handleErrorWithToast({
          id: `disconnectIntegration-${Date.now().toString()}`,
          title: "Failed to disconnect the integration.",
          error,
        });
      }
      setDialogState("none");
    },
    [handleErrorWithToast, tokensService, openToast, trackEvent]
  );

  /** Close the other tab if user cancel the integration enabling process from dashboard tab */
  const onCloseAuthorizationWindow = useCallback(() => {
    tokensService.closeAuthorizationWindow();
    setDialogState("none");
  }, [tokensService]);

  /** Callback when confirm button clicked on success dialog */
  function onSuccessDialogConfirm(): void {
    setDialogState("none");
  }

  /** Callback when cancel button clicked on in progress dialog */
  function onInProgressDialogCancel(): void {
    onCloseAuthorizationWindow();
  }

  /** Callback when confirm button clicked on disconnect dialog */
  function onDisconnectDialogConfirm(): void {
    void onDisableIntegration(integrationId);
  }

  /** Callback when cancel button clicked on disconnect dialog */
  function onDisconnectDialogCancel(): void {
    setDialogState("none");
  }

  return {
    allIntegrations,
    isLoadingIntegrations,
    onEnableIntegration,
    dialogState,
    setDialogState,
    onSuccessDialogConfirm,
    onInProgressDialogCancel,
    onDisconnectDialogConfirm,
    onDisconnectDialogCancel,
  };
}
